/**
 * Utility functions for lead data processing and formatting
 */

/**
 * Processes initial values that might be arrays, strings, or JSON strings
 * @param {any} initialValue - The value to process
 * @returns {Array|null} Processed array or null
 */
export function processInitialValue(initialValue) {
  if (Array.isArray(initialValue) || initialValue === null) {
    // If it's already an array or null, keep the value
    return initialValue;
  } else if (typeof initialValue === 'string') {
    try {
      // Try to parse the string as JSON
      let parsedValue = JSON.parse(initialValue);
      if (Array.isArray(parsedValue)) {
        return parsedValue;
      } else {
        // If the parsed value is not an array, split by commas
        return initialValue.split(',').map(item => item.trim());
      }
    } catch (e) {
      // If JSON parsing fails, split by commas
      return initialValue.split(',').map(item => item.trim());
    }
  } else {
    // Handle any other unexpected types, if necessary
    return null;  // or throw an error, depending on your needs
  }
}

/**
 * Formats a lead name for use in filenames or URLs
 * @param {string} name - The name to format
 * @returns {string} Formatted name
 */
export function formatLeadName(name) {
  // Handle undefined, null, or empty names
  if (!name || typeof name !== 'string') {
    return '';
  }

  // keep only letters and numbers
  name = name.replace(/[^a-zA-Z0-9 ]/g, '');

  // remove double or more spaces
  name = name.replace(/\s{2,}/g, ' ');

  if (name === '') {
    return '';
  }

  const removeSpecialChars = (str) => {
    if (str === undefined) {
      return '';
    }
    
    return str.replace(/[\u0300-\u036f]|[\uD800-\uDBFF][\uDC00-\uDFFF]|[\uD83C-\uDBFF\uDC00-\uDFFF]/g, '');
  }
  
  return name.split(' ')
    .map(item => item.startsWith('CRM') ?
      item :
      `${removeSpecialChars(item[0]).toLowerCase()}${item.slice(1)}`).join('-');
}

/**
 * Generates a filename for lead PDF export
 * @param {Object} data - Lead data containing id and name
 * @returns {string} Generated filename
 */
export function getFilename(data) {
  if (!data || !data.id) {
    return 'lead-unknown.pdf';
  }
  
  const formattedName = formatLeadName(data.name);
  const namePart = formattedName ? `-${formattedName}` : '';
  
  return `lead-${data.id}${namePart}.pdf`;
}

/**
 * Gets color for task status
 * @param {string} status - Task status
 * @returns {string} Color name
 */
export function getTaskColor(status) {
  return {
    'Vencido': 'red',
    'A vencer': 'yellow',
    'Hoje': 'green',
  }[status];
}